/**
 * ConfirmarOPModal — se abre automáticamente al adjuntar el pedido OP.
 *
 * Muestra SOLO lo que la app leyó del OP, producto por producto separado por
 * categoría de proveedor, para que Cecilia confirme que coincide con el pedido
 * real. No muestra nada de Finnegans: es únicamente una confirmación de lectura.
 */

import { useState, useEffect } from "react";
import { archivosOPs } from "../services/estadosObra";

const SECCIONES = [
  { key: "perfiles", label: "Perfiles" },
  { key: "refuerzos", label: "Refuerzos" },
  { key: "karpen", label: "Accesorios Karpen" },
  { key: "plaza", label: "Accesorios de plaza" },
  { key: "vidrios", label: "Vidrios" },
  { key: "telas", label: "Tela mosquitera" },
  { key: "terceros", label: "Anodizado / terceros" },
];

export default function ConfirmarOPModal({ obra, onConfirmar, onReemplazar, onClose, confirmando }) {
  const [estado, setEstado] = useState("cargando"); // cargando | listo | error
  const [error, setError] = useState("");
  const [grupos, setGrupos] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const archivos = archivosOPs(obra);
        if (archivos.length === 0) throw new Error("La obra no tiene ningún pedido OP adjunto");
        const { extraerItemsDeOPs } = await import("../services/parseArchivosObra");
        const items = await extraerItemsDeOPs(archivos);
        const g = { perfiles: [], refuerzos: [], karpen: [], plaza: [], vidrios: [], telas: [], terceros: [] };
        for (const it of items) {
          if (it.categoria === "perfiles" || it.categoria === "finnegans") {
            (/^R\d/i.test(it.alias) ? g.refuerzos : g.perfiles).push(it);
          } else if (g[it.categoria]) {
            g[it.categoria].push(it);
          }
        }
        setGrupos(g);
        setEstado("listo");
      } catch (e) {
        setError(e.message || String(e));
        setEstado("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function confirmar() {
    const cont = {};
    for (const s of SECCIONES) if (grupos[s.key]?.length) cont[s.key] = grupos[s.key].length;
    onConfirmar(cont);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 270, padding: 12,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, width: "100%", maxWidth: 760,
          maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <div style={{ padding: "16px 18px 10px" }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>📋 Confirmá la lectura del pedido OP · obra {obra.numero}</h2>
          <p style={{ fontSize: 12.5, color: "#64748b", margin: "6px 0 0" }}>
            Esto es lo que la app leyó del OP. Revisá que los productos y cantidades coincidan
            con el pedido real antes de continuar.
          </p>
        </div>

        <div style={{ overflowY: "auto", padding: "0 18px", flex: 1 }}>
          {estado === "cargando" && <div style={{ color: "#64748b", padding: 24, textAlign: "center" }}>Leyendo el pedido OP…</div>}
          {estado === "error" && (
            <div style={{ color: "#b91c1c", background: "#fee2e2", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
              {error}
            </div>
          )}
          {estado === "listo" && SECCIONES.filter(s => grupos[s.key]?.length > 0).map(s => {
            const items = grupos[s.key];
            const totalCant = items.reduce((sum, it) => sum + it.cant, 0);
            return (
              <div key={s.key} style={{ marginBottom: 10 }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12,
                  fontWeight: 700, color: "#475569", background: "#f1f5f9",
                  borderRadius: 6, padding: "5px 10px", marginBottom: 3, flexWrap: "wrap",
                }}>
                  <span>{s.label}</span>
                  <span style={{ color: "#94a3b8", fontWeight: 600 }}>{items.length} ítems · {Math.round(totalCant * 100) / 100} unid.</span>
                </div>
                {items.map((it, i) => (
                  <div key={i} style={{
                    display: "grid", gridTemplateColumns: "44px 90px 1fr", gap: 8, fontSize: 12.5,
                    color: "#334155", padding: "3px 10px",
                    borderBottom: i < items.length - 1 ? "1px solid #f8fafc" : "none",
                    alignItems: "baseline",
                  }}>
                    <span style={{ fontWeight: 700, textAlign: "right", color: "#0f172a" }}>{it.cant}×</span>
                    <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{it.alias}</span>
                    <span style={{ color: "#64748b", overflowWrap: "anywhere" }}>{it.detalle}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {onReemplazar && (
            <button className="btn btn--ghost" style={{ fontSize: 13, color: "#b91c1c" }} onClick={onReemplazar} disabled={confirmando}>
              No coincide — reemplazar OP
            </button>
          )}
          <button
            className="btn"
            style={{ fontSize: 13.5, background: "#16a34a", color: "#fff", border: "none" }}
            onClick={confirmar}
            disabled={estado !== "listo" || confirmando}
          >
            {confirmando ? "Confirmando…" : "✓ Confirmar lectura del OP"}
          </button>
        </div>
      </div>
    </div>
  );
}
